import React, { ComponentProps, ReactElement } from 'react';

import PropTypes from 'prop-types';
import styled from 'styled-components';

import { stylePropType, StylableComponent } from '@adsk/alloy-react-helpers';
import Icon, { IconType } from '@adsk/alloy-react-icon';
import ProgressRing from '@adsk/alloy-react-progress-ring';
import theme from '@adsk/alloy-react-theme';
import Tooltip, { OverflowTooltip } from '@adsk/alloy-react-tooltip';

import { Variant, FILE_HANDLER_VARIANTS, variantPropType } from './consts';
import Name from './layout/Name';

type FileHandlerProps = StylableComponent<HTMLDivElement> & {
  name: string;
  error?: string;
  metadata?: string;
  variant?: Variant;
  icon?: IconType;
  renderFileName?: (props: ComponentProps<typeof Name>) => ReactElement | null;
  renderMainAction?: () => ReactElement | null;
  renderSecondaryAction?: () => ReactElement | null;
};

const Action = styled.div({
  height: 24,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  flexShrink: 0,
});

/**
 * A FileHandler
 * docs.
 */
const FileHandler = React.forwardRef<HTMLDivElement, FileHandlerProps>(
  (
    {
      className,
      style,
      name,
      error,
      metadata,
      variant,
      children,
      icon,
      renderFileName = (props) => <Name {...props} />,
      renderMainAction,
      renderSecondaryAction,
      ...rest
    },
    ref
  ) => {
    const finalVariant = variant || FILE_HANDLER_VARIANTS.DEFAULT;
    const disabled = [
      FILE_HANDLER_VARIANTS.LOADING,
      FILE_HANDLER_VARIANTS.REMOVED,
    ].includes(finalVariant);

    return (
      <div
        {...rest}
        ref={ref}
        className={className}
        css={[
          {
            padding: '12px 16px',
            flex: 1,
            display: 'flex',
            boxShadow: '0px 1px 0px 0px rgba(33, 33, 33, 0.08)',
          },
          style,
        ]}
      >
        {icon && (
          <Icon
            style={{ marginRight: 8, flexShrink: 0 }}
            type={icon}
            color={
              disabled ? theme.colors.charcoal500 : theme.colors.charcoal900
            }
          />
        )}
        <div css={[theme.typography.ellipsis, { flex: 1 }]}>
          {renderFileName({ disabled, variant: finalVariant, children: name })}
          {!disabled && metadata && (
            <div
              css={[
                theme.typography.caption,
                { color: theme.colors.charcoal500 },
              ]}
            >
              <OverflowTooltip>{metadata}</OverflowTooltip>
            </div>
          )}
        </div>
        {[FILE_HANDLER_VARIANTS.DEFAULT, FILE_HANDLER_VARIANTS.ERROR].includes(
          finalVariant
        ) &&
          renderMainAction && (
            <Action style={{ marginLeft: 16 }}>{renderMainAction()}</Action>
          )}
        {[
          FILE_HANDLER_VARIANTS.LOADING,
          FILE_HANDLER_VARIANTS.SUCCESS,
          FILE_HANDLER_VARIANTS.ERROR,
        ].includes(finalVariant) && (
          <Action style={{ marginLeft: 8 }}>
            {finalVariant === FILE_HANDLER_VARIANTS.LOADING && (
              <ProgressRing
                size={ProgressRing.SIZES.XSMALL}
                data-testid="FileHandlerLoader"
              />
            )}
            {finalVariant === FILE_HANDLER_VARIANTS.SUCCESS && (
              <Icon
                type={Icon.TYPES.CHECKMARK_CIRCLE_FILLED}
                color={theme.colors.green500}
              />
            )}
            {finalVariant === FILE_HANDLER_VARIANTS.ERROR && (
              <Tooltip content={error} shouldWrapChildren={false}>
                <Icon
                  type={Icon.TYPES.ALERT_WARNING_FILLED}
                  color={theme.colors.red500}
                />
              </Tooltip>
            )}
          </Action>
        )}
        {renderSecondaryAction && (
          <Action style={{ marginLeft: 16 }}>{renderSecondaryAction()}</Action>
        )}
      </div>
    );
  }
);

FileHandler.displayName = 'FileHandler';
FileHandler.defaultProps = {
  variant: FILE_HANDLER_VARIANTS.DEFAULT,
};

FileHandler.propTypes = {
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /** Content */
  children: PropTypes.node,
  /** The style variant */
  variant: variantPropType,
  /** File name */
  name: PropTypes.string.isRequired,
  /** Error to show in tooltip */
  error: PropTypes.string,
  /** File name */
  metadata: PropTypes.string,
  /** Icon type */
  icon: PropTypes.oneOf<IconType>(Object.values(Icon.TYPES)),
  /** Render custom name component */
  renderFileName: PropTypes.func,
  /** Render main action */
  renderMainAction: PropTypes.func,
  /** Render secondary action */
  renderSecondaryAction: PropTypes.func,
};

export default Object.assign(FileHandler, {
  ICON_TYPES: Icon.TYPES,
  VARIANTS: FILE_HANDLER_VARIANTS,
});
